import Vue from 'vue';
import app from 'app';

const UserModule = (state) => {
  state.ssoModal = false;
  return {
    namespaced: true,
    state,

    // @param [state, getters, rootState, rootGetters]
    getters: {
      selectedOrganization(state) {
        return state.organizations.find(
          (org) => org.organizationId === state.selectedOrganizationId
        );
      },
      type(state) {
        return {
          CUSTOMER: 'CUSTOMER' === state.employeeType,
          WOCHIT_OPS: 'WOCHIT_OPS' === state.employeeType,
          WOCHIT_INTERNAL: 'WOCHIT_INTERNAL' === state.employeeType,
          ACCOUNT_MANAGER: 'ACCOUNT_MANAGER' === state.employeeType,
          OBSOLETE: 'OBSOLETE' === state.employeeType,
          // moderator can see only article that belong to himself
          MINI_PRODUCTION_LINE: 'MINI_PRODUCTION_LINE' === state.employeeType,
          INDIVIDUAL_CREATOR: 'INDIVIDUAL_CREATOR' === state.employeeType,
          STORYBOARDER: 'CHANNEL_STORYBOARD_CREATOR' === state.employeeType,
        };
      },
      id(state) {
        return state.employeeId;
      },
      box(state) {
        const env = {
          UNKNOWN: 0,
          DEV: 1,
          TEST: 2,
          PROD: 3,
          state: 0, // default unknown
          isProd: function () {
            return this.state === this.PROD;
          },
          isTest: function () {
            return this.state === this.TEST;
          },
          isDev: function () {
            return this.state === this.DEV;
          },
          isSecure: function () {
            return /^https:$/i.test(window.location.protocol);
          },
        };

        if ('prod' === serverProfile.environment) {
          env.state = env.PROD;
        } else if ('test' === serverProfile.environment) {
          env.state = env.TEST;
        } else if ('dev' === serverProfile.environment) {
          env.state = env.DEV;
        }
        return env;
      },
      isFirstLogin(state) {
        const aData = state.lastLoginTime;
        return _.isNull(aData);
      },
      useAutosave(state, getters) {
        if (app.sudo.autosave.override) {
          return app.sudo.autosave.enabled;
        } else {
          return getters.hasPermission('USE_AUTO_SAVE');
        }
      },
      hasPermission: (state) => (input) => {
        let output = false;
        const aData = state.permissions;

        if (!_.isEmpty(input) && !_.isEmpty(aData) && _.isArray(aData)) {
          if (_.isString(input)) {
            output = aData.indexOf(input.toUpperCase()) >= 0;
          } else if (_.isArray(input)) {
            // support multiple values check, return true if ALL matches!
            let bFound = false;
            _.each(input, function (value) {
              bFound = aData.indexOf(value.toUpperCase()) >= 0;
              return bFound;
            });
            output = bFound;
          }
        }

        return output;
      },

      userName(store) {
        let name = `${store.firstName} ${store.lastName}`;

        if (!name.trim().length) {
          name = store.username || store.userId;
        }

        return name;
      },

      isSSO(state) {
        return state.ssoModel;
      },

      editorDefault(state) {
        return {
          programmerId: window.localStorage.getItem(
            state.employeeId + '_' + 'programmerId'
          ),
          language: window.localStorage.getItem(
            state.employeeId + '_' + 'language'
          ),
          renderPublishDate: window.localStorage.getItem(
            state.employeeId + '_' + 'renderPublishDate'
          ),
        };
      },

      xhrHeaders(state, getters, rootState, rootGetters) {
        const authorization = sessionStorage.getItem('Authorization');

        return {
          ...(authorization ? { Authorization: authorization } : null),
          ...(state.channelId ? { 'channel-id': state.channelId } : null),
          ...(state.selectedOrganizationId
            ? { 'organization-id': state.selectedOrganizationId }
            : null),
        };
      },

      isImpededTemplatizer() {
        // an impeded user, who can create and use only templates for shortcut
        return (
          sessionStorage.getItem('authentication-manager') ===
          'self-serve-admin'
        );
      },

      isTemplateSpecialist(state, getters) {
        return getters.type.STORYBOARDER || getters.isImpededTemplatizer;
      },
    },

    mutations: {
      update(state, options) {
        Object.entries(options).forEach((arr) => {
          const [key, value] = arr;
          Vue.set(state, key, value);
        });
      },
      editorDefault(state, options) {
        if (options) {
          _.forEach(options, function (value, key) {
            window.localStorage.setItem(state.employeeId + '_' + key, value);
          });
        }
      },
    },

    actions: {
      $INIT_MODULE({ state, getters, rootGetters }) {
        if (getters.type.INDIVIDUAL_CREATOR && !getters.isImpededTemplatizer) {
          // kickoff individuals coming from WI platforms
          app.navigateWochitInsideIndividuals();
          // point of "no return"
          return;
        }

        // check if user made sso login
        app.api.handshake.get().done((res) => {
          if (res && !_.isEmpty(res.tokens)) {
            const defaultToken = _.find(res.tokens, {
              channelId: state.channelId,
            });
            if (defaultToken && !_.isEmpty(defaultToken.sessionIndex)) {
              state.ssoModal = true;
            }
          }
        });

        state.organizations.forEach((org) => {
          org.brands.sort((a, b) => {
            a.name = a.name || '';
            b.name = b.name || '';
            return a.name.localeCompare(b.name);
          });
          org.brands.forEach((brand) => {
            Vue.set(
              brand,
              '_mixedId',
              `${brand.brandId}/${brand.programmerId}`
            );
          });
        });
      },

      selectOrganization({ state }, newId) {
        localStorage.setItem('selectedOrganizationId', newId);
        window.location.href =
          window.location.origin + window.location.pathname;
      },

      logout({ state, getters }, options) {
        options = Object.assign(
          {
            /** @type: {boolean}
             *  true - "soft logout" - without redirecting to anywhere and without changing UI
             */
            soft: false,
          },
          options
        );

        app.delayedSpinner.show();

        const authorization = sessionStorage.getItem('Authorization');
        if (authorization) {
          sessionStorage.removeItem('Authorization');
        }
        if (!state.channelId) {
          sessionStorage.removeItem('channelId');
        }

        // get redirect URL to follow
        fetch(`${serverProfile.authUrl}/api/v1/logout?soft=${options.soft}`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: { ...getters.xhrHeaders },
        })
          .then((res) => {
            if (res.ok || 401 === res.status) {
              app.broadcast.emit('bc-user-logout');
              !options.soft && app.stop();
              return res.json();
            } else {
              app.delayedSpinner.hide();
            }
          })
          .then((res) => {
            if (res && 401 === res.code) {
              // trying to logout while already logged out
              !options.soft && app.navigate2Login({ allowGetBack: false });
            } else if (res && res.redirectUrl && !options.soft) {
              if (app.rootStore.getters['user/box'].isDev()) {
                // ignore redirectUrl from server while in Dev env
                app.navigate2Login({ allowGetBack: false });
              } else {
                app.navigate2Login({
                  redirectUrl: res.redirectUrl,
                  allowGetBack: false,
                });
              }
            }
          });
      },
    },
  };
};
export default UserModule;
